import React, { useState } from 'react';
import { UserProfile, signInWithGoogle } from '../lib/firebase';
import { UserRole } from '../types/user';
import { sanitizeSessionUser } from '../utils/rbac';
import { Language } from '../types/kundali';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  Sparkles,
  ShieldCheck,
  UserCheck,
  Mail,
  KeyRound,
  ArrowRight,
  Shield,
  User,
  Copy,
  CheckCircle2,
  RefreshCw,
  Compass,
  Star,
  BookOpen,
  Send,
  Lock,
  Globe,
  MessageSquare,
  Phone,
  Eye,
  EyeOff,
  UserPlus,
  LogIn,
  Check
} from 'lucide-react';

interface LoginPageProps {
  currentUser: UserProfile | null;
  onLoginSuccess: (user: UserProfile) => void;
  onContinueAsGuest: () => void;
  onOpenContactModal: () => void;
  lang: Language;
  setLang: (lang: Language) => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({
  currentUser,
  onLoginSuccess,
  onContinueAsGuest,
  onOpenContactModal,
  lang,
  setLang
}) => {
  // Mode: 'login' | 'register'
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

  // Login Options
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp' | 'google'>('password');
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // OTP Login Sub-state
  const [otpTargetChannel, setOtpTargetChannel] = useState<'mobile' | 'email'>('mobile');
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [loginOtpStep, setLoginOtpStep] = useState<'input' | 'verify'>('input');
  const [loginOtpCode, setLoginOtpCode] = useState('');
  const [generatedLoginOtp, setGeneratedLoginOtp] = useState<string | null>(null);

  // Register Fields
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regMobile, setRegMobile] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regOtpStep, setRegOtpStep] = useState<'send' | 'verify'>('send');
  const [regMobileOtp, setRegMobileOtp] = useState('');
  const [generatedRegOtp, setGeneratedRegOtp] = useState<string | null>(null);
  const [mobileVerified, setMobileVerified] = useState(false);

  // Status & Error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // --- HANDLERS FOR REGISTER ---
  const handleSendRegMobileOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!regMobile || regMobile.replace(/\D/g, '').length < 7) {
      setError(lang === 'hi' ? 'कृपया एक वैध 10-अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: regMobile.trim(),
          channel: 'mobile'
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send Mobile OTP');

      setGeneratedRegOtp(data.otpCode);
      setRegOtpStep('verify');
      setSuccessMsg(lang === 'hi' ? 'मोबाइल सत्यापन OTP कोड भेजा गया है' : 'Mobile verification code sent!');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code to mobile number.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegMobileOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regMobileOtp || regMobileOtp.trim().length !== 6) {
      setError(lang === 'hi' ? 'कृपया 6 अंकों का OTP दर्ज करें' : 'Please enter the 6-digit OTP code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: regMobile.trim(),
          code: regMobileOtp.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid OTP code');

      setMobileVerified(true);
      setSuccessMsg(lang === 'hi' ? 'मोबाइल नंबर सफलतापूर्वक सत्यापित हो गया!' : 'Mobile number verified successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to verify mobile OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!regName.trim()) {
      setError(lang === 'hi' ? 'कृपया अपना नाम दर्ज करें' : 'Please enter your full name');
      return;
    }
    if (!regEmail || !regEmail.includes('@')) {
      setError(lang === 'hi' ? 'कृपया एक वैध ईमेल पता दर्ज करें' : 'Please enter a valid email address');
      return;
    }
    if (!regMobile || regMobile.replace(/\D/g, '').length < 7) {
      setError(lang === 'hi' ? 'कृपया अपना मोबाइल नंबर दर्ज करें' : 'Please enter your mobile number');
      return;
    }
    if (!regPassword || regPassword.length < 6) {
      setError(lang === 'hi' ? 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए' : 'Password must be at least 6 characters');
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setError(lang === 'hi' ? 'पासवर्ड मेल नहीं खा रहे हैं' : 'Passwords do not match');
      return;
    }
    if (!regMobileOtp || regMobileOtp.trim().length !== 6) {
      setError(lang === 'hi' ? 'कृपया पहले मोबाइल नंबर पर प्राप्त 6-अंकों का OTP कोड भरें' : 'Please verify mobile number with 6-digit OTP code first');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: regName.trim(),
          email: regEmail.trim(),
          mobile: regMobile.trim(),
          password: regPassword,
          mobileOtp: regMobileOtp.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');

      const userProfile = sanitizeSessionUser(data.user) || data.user;
      onLoginSuccess(userProfile);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLERS FOR LOGIN ---
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginIdentifier.trim()) {
      setError(lang === 'hi' ? 'कृपया ईमेल अथवा मोबाइल नंबर दर्ज करें' : 'Please enter your User ID, Email or Mobile number');
      return;
    }
    if (!loginPassword) {
      setError(lang === 'hi' ? 'कृपया अपना पासवर्ड दर्ज करें' : 'Please enter your password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/login-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: loginIdentifier.trim(),
          password: loginPassword
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      const userProfile = sanitizeSessionUser(data.user) || data.user;
      onLoginSuccess(userProfile);
    } catch (err: any) {
      setError(err.message || 'Failed to log in with password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLoginOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpIdentifier.trim()) {
      setError(
        otpTargetChannel === 'mobile'
          ? (lang === 'hi' ? 'कृपया मोबाइल नंबर दर्ज करें' : 'Please enter your mobile number')
          : (lang === 'hi' ? 'कृपया ईमेल पता दर्ज करें' : 'Please enter your email address')
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: otpTargetChannel === 'email' ? otpIdentifier.trim() : undefined,
          mobile: otpTargetChannel === 'mobile' ? otpIdentifier.trim() : undefined,
          channel: otpTargetChannel
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP');

      setGeneratedLoginOtp(data.otpCode);
      setLoginOtpStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLoginOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginOtpCode || loginOtpCode.trim().length !== 6) {
      setError(lang === 'hi' ? 'कृपया 6 अंकों का OTP दर्ज करें' : 'Please enter the 6-digit OTP code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: otpIdentifier.trim(),
          code: loginOtpCode.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid OTP code');

      const userProfile = sanitizeSessionUser(data.user) || data.user;
      onLoginSuccess(userProfile);
    } catch (err: any) {
      setError(err.message || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      const userProfile = sanitizeSessionUser(user) || user;
      onLoginSuccess(userProfile);
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-astro-card text-astro-text flex flex-col justify-between selection:bg-astro-primary selection:text-white relative">
      {/* Background Glow */}
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-astro-primary/5 via-astro-card to-astro-card" />

      {/* Top Header */}
      <header className="relative z-10 max-w-7xl w-full mx-auto px-4 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-astro-primary/10 border border-astro-primary/30 flex items-center justify-center text-astro-primary shadow-md">
            <Compass className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-serif tracking-wide text-astro-text">
              {lang === 'hi' ? 'वेदांग ज्योतिषम्' : 'Vedang Jyotisham'}
            </h1>
            <p className="text-[10px] text-astro-primary font-semibold uppercase tracking-wider">
              {lang === 'hi' ? 'वेदांग ज्योतिषम् एवं कुण्डली' : 'Vedic Astrology & Kundali Engine'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher lang={lang} setLang={setLang} />
          <button
            onClick={onOpenContactModal}
            className="px-3.5 py-2 rounded-xl bg-astro-bg hover:bg-astro-card border border-astro-border hover:border-astro-primary/50 text-xs text-astro-primary font-semibold flex items-center gap-2 transition-all shadow-sm"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>{lang === 'hi' ? 'संपर्क करें (Gmail)' : 'Contact Form'}</span>
          </button>

          <button
            onClick={onContinueAsGuest}
            className="px-4 py-2 rounded-xl bg-astro-primary/10 hover:bg-astro-primary/20 border border-astro-primary/30 text-xs text-astro-primary font-bold transition-all flex items-center gap-1.5"
          >
            <span>{lang === 'hi' ? 'बिना लॉगिन ऐप देखें' : 'Explore Workspace'}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <main className="relative z-10 max-w-6xl w-full mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center my-auto">
        {/* Left Column: Brand Hero */}
        <div className="lg:col-span-6 space-y-6 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-astro-primary/10 border border-astro-primary/30 text-astro-primary text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
            <span>{lang === 'hi' ? 'प्रामाणिक महर्षि पराशर सिद्धांत' : 'Authentic Maharishi Parashari Engine'}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif text-astro-text leading-tight">
            {lang === 'hi'
              ? 'आपकी व्यक्तिगत वैदिक कुण्डली एवं गोचर ज्ञान'
              : 'Precision Vedic Kundali & Astrological Insights'}
          </h2>

          <p className="text-sm text-astro-secondary-text leading-relaxed max-w-lg mx-auto lg:mx-0">
            {lang === 'hi'
              ? 'सुरक्षित अकाउंट बनाकर अपनी कुण्डली सहेजें, विंशोत्तरी दशा देखें, अष्टकूट गुण मिलान करें और AI ज्योतिषाचार्य से परामर्श लें।'
              : 'Create or sign in to your profile to generate birth charts, store saved Kundalis in the Cloud Vault, inspect Dasha timelines, and perform Gun Milan.'}
          </p>

          <div className="grid grid-cols-2 gap-3 pt-2 text-left">
            <div className="p-3.5 bg-astro-bg rounded-2xl border border-astro-border shadow-sm">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-xs mb-1">
                <Star className="w-4 h-4 text-astro-accent" />
                <span>{lang === 'hi' ? 'उत्तर व दक्षिण चार्ट' : 'North & South Charts'}</span>
              </div>
              <p className="text-[11px] text-astro-secondary-text">High-precision planetary degrees & planetary dignity analysis.</p>
            </div>

            <div className="p-3.5 bg-astro-bg rounded-2xl border border-astro-border shadow-sm">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-xs mb-1">
                <BookOpen className="w-4 h-4 text-astro-accent" />
                <span>{lang === 'hi' ? 'विंशोत्तरी महादशा' : 'Vimshottari Dashas'}</span>
              </div>
              <p className="text-[11px] text-astro-secondary-text">Complete 120-year Mahadasha & Antardasha timeline breakdown.</p>
            </div>

            <div className="p-3.5 bg-astro-bg rounded-2xl border border-astro-border shadow-sm">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-xs mb-1">
                <ShieldCheck className="w-4 h-4 text-[#6F9690]" />
                <span>{lang === 'hi' ? 'सुरक्षित क्लाउड वॉल्ट' : 'Cloud Kundali Vault'}</span>
              </div>
              <p className="text-[11px] text-astro-secondary-text">Encrypted cloud storage for personal chart history.</p>
            </div>

            <div className="p-3.5 bg-astro-bg rounded-2xl border border-astro-border shadow-sm">
              <div className="flex items-center gap-2 text-astro-primary font-bold text-xs mb-1">
                <MessageSquare className="w-4 h-4 text-[#6F9690]" />
                <span>{lang === 'hi' ? 'AI ज्योतिषाचार्य' : 'AI Astrologer'}</span>
              </div>
              <p className="text-[11px] text-astro-secondary-text">Advanced AI assistant trained on authentic Vedic scriptures.</p>
            </div>
          </div>
        </div>

        {/* Right Column: Auth Card with Registration & Login Tabs */}
        <div className="lg:col-span-6 bg-astro-bg border border-astro-border rounded-3xl p-6 sm:p-8 shadow-md relative">
          {/* Main Mode Tabs: Login vs Register */}
          <div className="flex bg-astro-card p-1.5 rounded-2xl border border-astro-border mb-6">
            <button
              type="button"
              onClick={() => {
                setActiveTab('login');
                setError(null);
                setSuccessMsg(null);
              }}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'login'
                  ? 'bg-astro-primary text-white shadow-md'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <LogIn className="w-4 h-4" />
              <span>{lang === 'hi' ? 'लॉग-इन करें' : 'Sign In'}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('register');
                setError(null);
                setSuccessMsg(null);
              }}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                activeTab === 'register'
                  ? 'bg-astro-primary text-white shadow-md'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              <span>{lang === 'hi' ? 'नया खाता बनाएं' : 'Register Account'}</span>
            </button>
          </div>

          {/* Feedback Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-600 text-xs">
              {error}
            </div>
          )}

          {successMsg && (
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-700 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* TAB 1: REGISTRATION FLOW */}
          {activeTab === 'register' && (
            <form onSubmit={handleRegisterUser} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'पूरा नाम *' : 'Full Name *'}
                </label>
                <div className="relative">
                  <User className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                  <input
                    type="text"
                    required
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full pl-9 pr-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'ईमेल पता *' : 'Email Address *'}
                </label>
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                  <input
                    type="email"
                    required
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    placeholder="rahul.sharma@example.com"
                    className="w-full pl-9 pr-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none font-mono"
                  />
                </div>
              </div>

              {/* Mobile Number & OTP Verification */}
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'मोबाइल नंबर (OTP सत्यापन के साथ) *' : 'Mobile Number (with OTP verification) *'}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                    <input
                      type="tel"
                      required
                      value={regMobile}
                      onChange={(e) => {
                        setRegMobile(e.target.value);
                        setMobileVerified(false);
                      }}
                      placeholder="9876543210"
                      className="w-full pl-9 pr-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none font-mono"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendRegMobileOtp}
                    disabled={loading || !regMobile}
                    className="px-3.5 py-2.5 bg-astro-primary/10 hover:bg-astro-primary/20 border border-astro-primary/30 text-astro-primary font-semibold rounded-xl text-xs transition-all shrink-0 flex items-center gap-1"
                  >
                    <Send className="w-3 h-3" />
                    <span>{regOtpStep === 'verify' ? 'Resend OTP' : 'Send OTP'}</span>
                  </button>
                </div>

                {generatedRegOtp && (
                  <div className="mt-2 p-2.5 bg-astro-primary/10 border border-astro-primary/20 rounded-xl flex items-center justify-between text-xs text-astro-primary">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                      <span>
                        OTP Code: <strong className="font-mono text-astro-text">{generatedRegOtp}</strong>
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRegMobileOtp(generatedRegOtp)}
                      className="text-[10px] bg-astro-primary px-2 py-0.5 rounded text-white hover:bg-[#123d3c] font-semibold transition-all"
                    >
                      Auto-fill
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile OTP Input Field */}
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'मोबाइल 6-अंकों का OTP कोड *' : '6-Digit Mobile Verification OTP *'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    maxLength={6}
                    value={regMobileOtp}
                    onChange={(e) => setRegMobileOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="e.g. 123456"
                    className="flex-1 px-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-primary font-mono font-bold tracking-widest focus:border-astro-primary focus:outline-none"
                  />
                  {regOtpStep === 'verify' && !mobileVerified && (
                    <button
                      type="button"
                      onClick={handleVerifyRegMobileOtp}
                      disabled={loading || regMobileOtp.length !== 6}
                      className="px-3.5 py-2.5 bg-emerald-600 text-white font-semibold rounded-xl text-xs transition-all shrink-0"
                    >
                      Verify OTP
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-astro-secondary-text mt-1">
                  Universal test code <code className="text-astro-primary font-mono">123456</code> is also supported.
                </p>
              </div>

              {/* Password & Confirm Password */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                    {lang === 'hi' ? 'पासवर्ड बनाएं *' : 'Create Password *'}
                  </label>
                  <div className="relative">
                    <Lock className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                    <input
                      type={showRegPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-8 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowRegPassword(!showRegPassword)}
                      className="absolute right-2.5 top-2.5 text-astro-secondary-text hover:text-astro-text"
                    >
                      {showRegPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                    {lang === 'hi' ? 'पासवर्ड की पुष्टि करें *' : 'Confirm Password *'}
                  </label>
                  <div className="relative">
                    <Lock className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                    <input
                      type={showRegPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={regConfirmPassword}
                      onChange={(e) => setRegConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-astro-primary text-white font-bold rounded-xl text-xs hover:bg-[#123d3c] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-md mt-2"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                <span>{loading ? 'Creating Account...' : 'Complete Registration'}</span>
              </button>
            </form>
          )}

          {/* TAB 2: SIGN IN FLOW */}
          {activeTab === 'login' && (
            <div>
              {/* Login Method Sub-Tabs */}
              <div className="flex bg-astro-card p-1 rounded-xl border border-astro-border mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('password');
                    setError(null);
                  }}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                    loginMethod === 'password'
                      ? 'bg-astro-primary text-white shadow'
                      : 'text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  <Lock className="w-3 h-3" />
                  <span>Password Login</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('otp');
                    setError(null);
                  }}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                    loginMethod === 'otp'
                      ? 'bg-astro-primary text-white shadow'
                      : 'text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  <KeyRound className="w-3 h-3" />
                  <span>OTP Login</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    handleGoogleSignIn();
                  }}
                  disabled={loading}
                  className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50"
                  title="Sign In directly with Google"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.11-6.72-4.96H1.29v3.15C3.26 21.3 7.31 24 12 24z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.28 14.24c-.25-.72-.38-1.49-.38-2.24s.13-1.52.38-2.24V6.61H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.39l3.99-3.15z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.61l3.99 3.15c.95-2.85 3.6-4.96 6.72-4.96z"
                    />
                  </svg>
                  <span>{loading ? 'Connecting...' : 'Google'}</span>
                </button>
              </div>

              {/* METHOD A: PASSWORD LOGIN */}
              {loginMethod === 'password' && (
                <form onSubmit={handlePasswordLogin} className="space-y-3.5">
                  <div className="mb-2">
                    <h3 className="text-base sm:text-lg font-bold font-serif text-astro-text">
                      {lang === 'hi' ? 'पुनः स्वागत है' : 'Welcome back'}
                    </h3>
                    <p className="text-xs text-astro-secondary-text mt-0.5">
                      {lang === 'hi'
                        ? 'अपने ज्योतिषीय कार्यक्षेत्र में प्रवेश के लिए साइन इन करें।'
                        : 'Sign in to continue to your astrology workspace.'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                      {lang === 'hi' ? 'यूज़र आईडी / ईमेल अथवा मोबाइल *' : 'User ID / Email or Mobile *'}
                    </label>
                    <div className="relative">
                      <User className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                      <input
                        type="text"
                        required
                        value={loginIdentifier}
                        onChange={(e) => setLoginIdentifier(e.target.value)}
                        placeholder={lang === 'hi' ? 'यूज़र आईडी, ईमेल अथवा मोबाइल' : 'User ID, Email or Mobile Number'}
                        className="w-full pl-9 pr-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                      {lang === 'hi' ? 'पासवर्ड *' : 'Password *'}
                    </label>
                    <div className="relative">
                      <Lock className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                      <input
                        type={showLoginPassword ? 'text' : 'password'}
                        required
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-9 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-3 text-astro-secondary-text hover:text-astro-text"
                        title={showLoginPassword ? 'Hide Password' : 'Show Password'}
                      >
                        {showLoginPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember Me & Forgot Password Row */}
                  <div className="flex items-center justify-between text-xs pt-0.5">
                    <label className="flex items-center gap-1.5 cursor-pointer text-astro-secondary-text hover:text-astro-text">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="rounded border-astro-border text-astro-primary focus:ring-0 accent-astro-primary"
                      />
                      <span>{lang === 'hi' ? 'मुझे याद रखें' : 'Remember me'}</span>
                    </label>
                    <button
                      type="button"
                      onClick={onOpenContactModal}
                      className="text-astro-primary hover:underline font-medium text-xs"
                    >
                      {lang === 'hi' ? 'पासवर्ड भूल गए?' : 'Forgot Password?'}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-astro-primary text-white font-bold rounded-xl text-xs hover:bg-[#123d3c] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm mt-1"
                  >
                    {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                    <span>{loading ? (lang === 'hi' ? 'प्रवेश हो रहा है...' : 'Signing In...') : (lang === 'hi' ? 'साइन इन करें' : 'Sign In')}</span>
                  </button>

                  {/* Fast Testing Accounts Quick-Fill */}
                  <div className="mt-3 pt-3 border-t border-astro-border/70">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-astro-secondary-text mb-1.5 flex items-center justify-between">
                      <span>{lang === 'hi' ? 'त्वरित परीक्षण खाते (QA Quick-Fill)' : 'Quick Test Accounts'}</span>
                      <Sparkles className="w-3 h-3 text-astro-accent" />
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setLoginIdentifier('tester@vedangjyotisham.com');
                          setLoginPassword('tester123');
                          setError(null);
                        }}
                        className="p-1.5 bg-astro-card hover:bg-astro-border/60 border border-astro-border rounded-lg text-left transition-colors"
                        title="Tester Identity with Role Switch authorization"
                      >
                        <div className="font-bold text-[11px] text-astro-primary">Tester</div>
                        <div className="text-[9px] text-astro-secondary-text">QA Testing</div>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setLoginIdentifier('astrologer@vedangjyotisham.com');
                          setLoginPassword('astro123');
                          setError(null);
                        }}
                        className="p-1.5 bg-astro-card hover:bg-astro-border/60 border border-astro-border rounded-lg text-left transition-colors"
                        title="Consulting Astrologer Account"
                      >
                        <div className="font-bold text-[11px] text-astro-primary">Astrologer</div>
                        <div className="text-[9px] text-astro-secondary-text">Jyotishi</div>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setLoginIdentifier('user@example.com');
                          setLoginPassword('user123');
                          setError(null);
                        }}
                        className="p-1.5 bg-astro-card hover:bg-astro-border/60 border border-astro-border rounded-lg text-left transition-colors"
                        title="Standard User Account"
                      >
                        <div className="font-bold text-[11px] text-astro-primary">User</div>
                        <div className="text-[9px] text-astro-secondary-text">Native Client</div>
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* METHOD B: OTP LOGIN */}
              {loginMethod === 'otp' && (
                <div>
                  <div className="flex justify-center gap-4 mb-3 text-xs">
                    <label className="flex items-center gap-1.5 cursor-pointer text-astro-secondary-text">
                      <input
                        type="radio"
                        name="otpChannel"
                        checked={otpTargetChannel === 'mobile'}
                        onChange={() => {
                          setOtpTargetChannel('mobile');
                          setLoginOtpStep('input');
                        }}
                        className="accent-astro-primary"
                      />
                      <span>OTP on Mobile</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer text-astro-secondary-text">
                      <input
                        type="radio"
                        name="otpChannel"
                        checked={otpTargetChannel === 'email'}
                        onChange={() => {
                          setOtpTargetChannel('email');
                          setLoginOtpStep('input');
                        }}
                        className="accent-astro-primary"
                      />
                      <span>OTP on Email</span>
                    </label>
                  </div>

                  {loginOtpStep === 'input' ? (
                    <form onSubmit={handleSendLoginOtp} className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                          {otpTargetChannel === 'mobile' ? 'Mobile Number *' : 'Email Address *'}
                        </label>
                        <div className="relative">
                          {otpTargetChannel === 'mobile' ? (
                            <Phone className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                          ) : (
                            <Mail className="w-3.5 h-3.5 absolute left-3 top-3 text-astro-secondary-text" />
                          )}
                          <input
                            type={otpTargetChannel === 'email' ? 'email' : 'tel'}
                            required
                            value={otpIdentifier}
                            onChange={(e) => setOtpIdentifier(e.target.value)}
                            placeholder={otpTargetChannel === 'mobile' ? '9876543210' : 'user@example.com'}
                            className="w-full pl-9 pr-3.5 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none font-mono"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-astro-primary text-white font-bold rounded-xl text-xs hover:bg-[#123d3c] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                      >
                        {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                        <span>{loading ? 'Generating OTP...' : `Send OTP to ${otpTargetChannel === 'mobile' ? 'Mobile' : 'Email'}`}</span>
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyLoginOtp} className="space-y-3">
                      {generatedLoginOtp && (
                        <div className="p-3 bg-astro-primary/10 border border-astro-primary/20 rounded-xl flex items-center justify-between text-xs text-astro-primary">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                            <div>
                              <span className="font-semibold block text-[10px]">OTP Code:</span>
                              <span className="font-mono text-sm font-bold tracking-widest text-astro-text">{generatedLoginOtp}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setLoginOtpCode(generatedLoginOtp)}
                            className="px-2.5 py-1 bg-white border border-astro-border text-astro-primary hover:bg-astro-card font-semibold rounded-lg transition-all text-[10px]"
                          >
                            Auto-Fill
                          </button>
                        </div>
                      )}

                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="text-xs font-semibold text-astro-secondary-text">Enter 6-Digit OTP</label>
                          <button
                            type="button"
                            onClick={() => setLoginOtpStep('input')}
                            className="text-[10px] text-astro-primary hover:underline"
                          >
                            Change Target
                          </button>
                        </div>
                        <input
                          type="text"
                          required
                          maxLength={6}
                          value={loginOtpCode}
                          onChange={(e) => setLoginOtpCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="123456"
                          className="w-full text-center tracking-[0.5em] text-lg font-mono py-2.5 bg-astro-card border border-astro-border rounded-xl text-astro-primary font-bold focus:border-astro-primary focus:outline-none"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-astro-primary text-white font-bold rounded-xl text-xs hover:bg-[#123d3c] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                      >
                        {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                        <span>{loading ? 'Verifying...' : 'Verify OTP & Log In'}</span>
                      </button>
                    </form>
                  )}
                </div>
              )}

              {/* METHOD C: GOOGLE SIGN IN */}
              {loginMethod === 'google' && (
                <div className="p-4 bg-astro-card rounded-2xl border border-astro-border text-center">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full py-3.5 px-4 bg-white text-astro-text font-bold rounded-xl hover:bg-astro-bg border border-astro-border transition-all flex items-center justify-center gap-3 shadow-sm disabled:opacity-50 text-xs"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.11-6.72-4.96H1.29v3.15C3.26 21.3 7.31 24 12 24z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.28 14.24c-.25-.72-.38-1.49-.38-2.24s.13-1.52.38-2.24V6.61H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.39l3.99-3.15z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.61l3.99 3.15c.95-2.85 3.6-4.96 6.72-4.96z"
                      />
                    </svg>
                    {loading ? 'Authenticating...' : 'Sign In with Google'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="pt-4 text-center">
            <button
              onClick={onContinueAsGuest}
              className="text-xs text-astro-secondary-text hover:text-astro-primary transition-colors underline"
            >
              {lang === 'hi' ? 'लॉगिन किए बिना जारी रखें (Guest Mode)' : 'Continue as Guest without login →'}
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-astro-border py-4 text-center text-[11px] text-astro-secondary-text">
        <p>© 2026 Vedanga Jyotisha • Contact: vedangajyotisha108@gmail.com</p>
      </footer>
    </div>
  );
};

